import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from './axiosInstance';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import BusinessIcon from '@mui/icons-material/Business';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { alpha, useTheme } from '@mui/material/styles';

import { useSnackbar } from '../providers/SnackbarProvider';
import InlineEdit from './ui/InlineEdit';
import CopyToClipboard from './ui/CopyToClipboard';
import { isValidEmail } from '../utils/validation';
import ActivityHub from './ActivityHub';
import FileRail from './ui/FileRail';
import SectionCard from './ui/SectionCard';
import { DetailSkeleton } from './ui/PageSkeleton';
import { useBreadcrumbs } from './BreadcrumbBar';
import {
  salutationOptions, titleOptions, typeOptions, priorityOptions,
} from '../constants/leadOptions';

const initials = (fn, ln) => `${fn?.[0] ?? ''}${ln?.[0] ?? ''}`.toUpperCase() || '?';

// Design 3.0: tiefere Töne (Pastell-Pill-Paare entstehen per alpha());
// Dark: aufgehellte Pendants der Dark-Semantikfarben — die tiefen Töne wären unleserlich
const PRIORITY_COLOR = (theme) => (theme.palette.mode === 'dark'
  ? { 'A+': '#F07A72', A: '#F0955E', B: '#E2A33B', C: '#98A2B5', D: '#7A8598' }
  : { 'A+': '#B91C1C', A: '#C2570C', B: '#B45309', C: '#647084', D: '#94A3B8' });

/**
 * Kontakt-Detailseite nach dem Zonen-Bauplan (Konzept 2.3) — bewusst OHNE
 * Phasenband: Kontakte haben keinen Verkaufsprozess (der lebt auf Lead bzw.
 * künftig auf dem Angebots-Objekt), nur Rolle, Erreichbarkeit und Verknüpfungen.
 *   A Kopfzeile   – Identität, ruhig und ohne Knopf-Stapel
 *   C Stammdaten & Ablage (linke Fläche) – Felder, Unternehmen, Dateien
 *   D Aktivitäten (rechte Fläche, das Rückgrat) – Composer · Geplant · Verlauf
 */
const ContactDetails = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const { id } = useParams();
  const [contact, setContact] = useState(null);
  const [account, setAccount] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(null);

  useBreadcrumbs(
    contact
      ? [{ label: 'Kontakte', path: '/contacts' }, { label: `${contact.first_name} ${contact.last_name}` }]
      : null
  );

  useEffect(() => {
    const loadContact = async () => {
      try {
        const response = await axiosInstance.get(`/contacts/${id}/`);
        setContact(response.data);
      } catch (error) {
        console.error('Error fetching contact data', error);
      }
    };
    loadContact();
  }, [id]);

  useEffect(() => {
    const loadAccount = async () => {
      if (!contact?.account) { setAccount(null); return; }
      try {
        const response = await axiosInstance.get(`/accounts/${contact.account}/`);
        setAccount(response.data);
      } catch (error) {
        console.error('Error fetching account:', error);
      }
    };
    loadAccount();
  }, [contact?.account]);

  const patchContact = async (data) => {
    try {
      const response = await axiosInstance.patch(`/contacts/${id}/`, data);
      setContact(response.data);
      showSnackbar('Erfolgreich aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating contact:', error);
      showSnackbar('Fehler beim Aktualisieren', 'error');
      throw error;
    }
  };
  const handleFieldUpdate = (field, value) => patchContact({ [field]: value });

  if (!contact) return <DetailSkeleton />;

  const isPrimary = account && account.contact_person === contact.id;
  const priorityColor = PRIORITY_COLOR(theme)[contact.priority] || theme.palette.tokens.muted;

  // Unterhalb von lg gestapelt (schmale Randspalte), ab lg Label neben Wert
  const fieldRow = (label, field, opts = {}) => (
    <Box key={field} sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '88px 1fr', md: '1fr', lg: '96px 1fr' },
      alignItems: 'center', columnGap: 1, rowGap: 0, py: { xs: 0.3, md: 0.55, lg: 0.3 },
    }}>
      <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
      <InlineEdit
        value={contact?.[field]}
        onSave={(v) => handleFieldUpdate(field, v)}
        selectOptions={opts.select}
        validate={opts.validate}
        multiline={opts.multiline}
        rows={opts.rows}
      />
    </Box>
  );

  const railLabel = (label, mt = 3) => (
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt, lineHeight: 1.6 }}>{label}</Typography>
  );

  return (
    <Box sx={{ pb: 3 }}>
      {/* A — Kopfzeile (eigene Fläche, v2.4) */}
      <SectionCard sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <Avatar sx={{ width: 58, height: 58, bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 800, fontSize: '1.3rem' }}>
          {initials(contact.first_name, contact.last_name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography variant="h4">{contact.first_name} {contact.last_name}</Typography>
            {contact.priority && (
              <Chip size="small" label={`Priorität ${contact.priority}`}
                sx={{ bgcolor: alpha(priorityColor, 0.13), color: priorityColor, fontWeight: 600 }} />
            )}
            {isPrimary && (
              <Chip size="small" label="Primärer Kontakt"
                sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.13), color: 'secondary.main', fontWeight: 600 }} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
            {account?.name && (<><BusinessIcon sx={{ fontSize: 16 }} />{account.name}{contact.position ? ` · ${contact.position}` : ''}</>)}
            {!account?.name && contact.position && (<><WorkOutlineIcon sx={{ fontSize: 16 }} />{contact.position}</>)}
            {contact.city && (<><PlaceOutlinedIcon sx={{ fontSize: 16, ml: (account?.name || contact.position) ? 1 : 0 }} />{contact.city}</>)}
          </Typography>
          {contact.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{contact.email}</Typography>
              <CopyToClipboard text={contact.email} tooltip="E-Mail kopieren" size="small" iconSx={{ fontSize: 14, color: 'text.secondary' }} />
            </Box>
          )}
        </Box>
      </Stack>
      </SectionCard>

      {/* Themen-Flächen auf getöntem Grund — bewusst ohne Phasenband */}
      <Grid container spacing={2.5} alignItems="flex-start">
        {/* C — Stammdaten & Ablage */}
        <Grid size={{ xs: 12, md: 4, lg: 3.5 }} order={{ xs: 2, md: 1 }}
          sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <SectionCard>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>Person</Typography>
          {fieldRow('Vorname', 'first_name', { validate: (v) => (!v?.trim() ? 'Vorname erforderlich' : null) })}
          {fieldRow('Nachname', 'last_name', { validate: (v) => (!v?.trim() ? 'Nachname erforderlich' : null) })}
          {fieldRow('Position', 'position')}
          {showAll && fieldRow('Anrede', 'salutation', { select: salutationOptions })}
          {showAll && fieldRow('Titel', 'title', { select: titleOptions })}

          {railLabel('Klassifizierung')}
          {fieldRow('Typ', 'type', { select: typeOptions })}
          {fieldRow('Priorität', 'priority', { select: priorityOptions })}

          {railLabel('Erreichbarkeit')}
          {fieldRow('E-Mail', 'email', { validate: (v) => (v && !isValidEmail(v) ? 'Ungültige E-Mail' : null) })}
          {fieldRow('Telefon', 'phone')}
          {fieldRow('Mobil', 'mobile')}
          {showAll && fieldRow('Webseite', 'website')}
          {showAll && fieldRow('Straße', 'street')}
          {showAll && fieldRow('PLZ', 'zip_code')}
          {showAll && fieldRow('Stadt', 'city')}

          {railLabel('Notizen')}
          <InlineEdit value={contact?.notes} onSave={(v) => handleFieldUpdate('notes', v)} multiline rows={4} />
          <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mt: 1, px: 0.5 }}>
            {showAll ? 'Weitere Felder ausblenden ▴' : 'Alle Felder anzeigen ▾'}
          </Button>

          </SectionCard>

          {/* Verknüpft — generisch angelegt: heute Unternehmen, künftig auch Angebote u. a. */}
          <SectionCard>
          {railLabel('Verknüpft', 0)}
          {account ? (
            <Box
              role="button" tabIndex={0}
              onClick={() => navigate(`/accounts/${account.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/accounts/${account.id}`); } }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.1, py: 0.6, cursor: 'pointer',
                borderRadius: '8px', px: 0.5, mx: -0.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>{account.name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {isPrimary ? 'Unternehmen · Primärer Kontakt' : 'Unternehmen'}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Keinem Unternehmen zugeordnet — die Zuordnung erfolgt auf der Unternehmensseite.
            </Typography>
          )}

          </SectionCard>

          <SectionCard>
          {railLabel(attachmentCount != null ? `Dateien (${attachmentCount})` : 'Dateien', 0)}
          <FileRail relatedType="contact" relatedId={contact.id} onCountChange={setAttachmentCount} />
          </SectionCard>
        </Grid>

        {/* D — Aktivitäten (das Rückgrat) */}
        <Grid size={{ xs: 12, md: 8, lg: 8.5 }} order={{ xs: 1, md: 2 }} sx={{ minWidth: 0 }}>
          <ActivityHub relatedType="contact" relatedId={id} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactDetails;
